import { Component, ReactNode } from 'react';
import { logError } from '../../../js/util/logger';
import StateProgramsError from './errors/StateProgramsError';

interface StateProgramsErrorBoundaryProps {
  /**
   * The program ID for error context
   */
  programId: string;

  /**
   * Optional program version for error context
   */
  programVersion?: string;

  /**
   * Optional visit ID for error context
   */
  visitId?: number;

  /**
   * Optional provider ID for error context
   */
  providerId?: number;

  /**
   * Optional custom error message (defaults to "An error occurred in state program")
   */
  errorMessage?: string;

  /**
   * Error reporter function (typically logError from logger)
   */
  errorReporter: typeof logError;

  /**
   * Optional callback when an error is caught (called after errorReporter)
   */
  onError?: (error: StateProgramsError) => void;

  /**
   * Optional fallback UI to render when error occurs
   */
  fallback?: ReactNode;

  children: ReactNode;
}

interface StateProgramsErrorBoundaryState {
  hasError: boolean;
  error: StateProgramsError | null;
}

/**
 * Error boundary that wraps errors in StateProgramsError with appropriate context
 * for state program operations.
 *
 * @example
 * ```tsx
 * <StateProgramsErrorBoundary
 *   programId="ca-wic"
 *   programVersion="v2"
 *   visitId={123}
 *   providerId={456}
 *   errorReporter={logError}
 * >
 *   <YourStateProgramComponent />
 * </StateProgramsErrorBoundary>
 * ```
 */
class StateProgramsErrorBoundary extends Component<
  StateProgramsErrorBoundaryProps,
  StateProgramsErrorBoundaryState
> {
  constructor(props: StateProgramsErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): StateProgramsErrorBoundaryState {
    return { hasError: true, error: error as StateProgramsError };
  }

  componentDidCatch(error: Error) {
    const { programId, programVersion, visitId, providerId, errorMessage, errorReporter, onError } =
      this.props;

    // Wrap in StateProgramsError if not already one
    const stateProgramError =
      error instanceof StateProgramsError
        ? error
        : new StateProgramsError(
            errorMessage || 'An error occurred rendering state-program features',
            error,
            programId,
            programVersion,
            visitId,
            providerId
          );

    // Log the error using injected error reporter
    errorReporter(stateProgramError);

    // Call optional error handler
    if (onError) {
      onError(stateProgramError);
    }

    // Update state with the wrapped error
    this.setState({ error: stateProgramError });
  }

  render() {
    if (this.state.hasError) {
      // Render fallback UI if provided, otherwise render nothing
      return this.props.fallback || null;
    }

    return this.props.children;
  }
}

export default StateProgramsErrorBoundary;
